import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wallet } from "lucide-react";

const chf = (v) => Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");

// Iter313: Offene Spesen (2060) – fliessen in den nächsten Lohnlauf oder werden per Bank ausbezahlt
export function ExpenseClaimsCard({ companyId, onPaid }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/expense-claims`);
      const all = r.data.items || [];
      setData({ ...r.data, items: all.filter((c) => c.status === "approved"), submitted: all.filter((c) => c.status === "submitted") });
    } catch { /* ignore */ }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  if (!data?.items?.length && !data?.submitted?.length) return null;

  const decide = async (c, approve) => {
    let reason = null;
    if (!approve) { reason = window.prompt("Grund der Ablehnung (sieht der Mitarbeiter im Portal):", "Nicht gemäss Spesenreglement"); if (!reason) return; }
    setBusy(true);
    try {
      if (approve) await apiClient.post(`/companies/${companyId}/expense-claims/${c.id}/approve`);
      else await apiClient.post(`/companies/${companyId}/expense-claims/${c.id}/reject`, { reason });
      toast.success(approve ? `Pauschale CHF ${chf(c.amount)} freigegeben – gebucht ${c.account_id} an 2060, Mitarbeiter informiert` : "Pauschale abgelehnt – Mitarbeiter informiert");
      await load();
      onPaid?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const pay = async () => {
    const ids = sel.length ? sel : data.items.map((c) => c.id);
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/expense-claims/pay`, { claim_ids: ids });
      toast.success(`Spesen CHF ${chf(r.data.amount)} per Bank erstattet (${r.data.count} Belege) – 2060 an Bank gebucht`);
      setSel([]);
      await load();
      onPaid?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auszahlung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border border-teal-200 bg-teal-50/40" data-testid="expense-claims-card">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="text-sm font-semibold flex items-center gap-2"><Wallet className="h-4 w-4" /> Offene Spesen an Mitarbeitende</h2>
        <div className="flex items-center gap-2">
          <Badge className="bg-teal-100 text-teal-800" data-testid="expense-claims-open-total">CHF {chf(data.open_total)} · {data.open_count} Belege</Badge>
          {data.items.length > 0 && <Button size="sm" variant="outline" disabled={busy} onClick={pay} data-testid="expense-claims-pay-button" title="Ausgewählte (oder alle) Spesen jetzt per Bank erstatten (2060 an Bank)">
            {busy ? "…" : sel.length ? `${sel.length} per Bank auszahlen` : "Alle per Bank auszahlen"}
          </Button>}
        </div>
      </div>
      <p className="text-xs text-slate-600 mb-2" data-testid="expense-claims-mode-hint">
        {data.mode === "payroll"
          ? "Standard: Diese Spesen werden automatisch AHV-frei mit dem nächsten regulären Lohnlauf des Mitarbeiters ausbezahlt (2060 an 2050)."
          : "Standard: Erstattung separat per Bank – Spesen fliessen nicht in den Lohnlauf."}
      </p>
      {data.submitted?.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2" data-testid="expense-claims-pending">
          <p className="text-xs font-semibold text-amber-900 mb-1">Pauschalen zur Freigabe ({data.submitted.length})</p>
          {data.submitted.map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-1 text-sm" data-testid={`expense-claim-pending-${c.id}`}>
              <span className="w-40 truncate font-medium">{c.employee_name}</span>
              <span className="w-24 text-slate-500">{formatDate(c.date)}</span>
              <span className="flex-1 truncate text-slate-700">{c.supplier_name}{c.note ? ` · ${c.note}` : ""}</span>
              <span className="font-mono">CHF {chf(c.amount)}</span>
              <Button size="sm" variant="outline" className="h-7 border-emerald-300 text-emerald-800" disabled={busy} onClick={() => decide(c, true)} data-testid={`expense-claim-approve-${c.id}`}>Freigeben</Button>
              <Button size="sm" variant="ghost" className="h-7 text-rose-700" disabled={busy} onClick={() => decide(c, false)} data-testid={`expense-claim-reject-${c.id}`}>Ablehnen</Button>
            </div>
          ))}
        </div>
      )}
      <div className="divide-y divide-teal-100">
        {data.items.map((c) => (
          <label key={c.id} className="flex items-center gap-3 py-1.5 text-sm cursor-pointer" data-testid={`expense-claim-row-${c.id}`}>
            <input type="checkbox" checked={sel.includes(c.id)} onChange={(e) => setSel(e.target.checked ? [...sel, c.id] : sel.filter((x) => x !== c.id))} data-testid={`expense-claim-select-${c.id}`} />
            <span className="w-40 truncate font-medium">{c.employee_name}</span>
            <span className="w-24 text-slate-500">{formatDate(c.date)}</span>
            <span className="flex-1 truncate text-slate-700">{c.supplier_name || "—"}{c.purpose_label ? ` · ${c.purpose_label}` : ""}{c.account_id ? ` · ${c.account_id}` : ""}</span>
            <span className="font-mono">CHF {chf(c.amount)}</span>
          </label>
        ))}
      </div>
    </Card>
  );
}
